/**
 * 城市系统类型定义
 * 用于管理城市手写设定与城市运行态
 */

import { z } from 'zod';
import { KnownBuildingSchema } from './building';

// ============================================
// 城市叙事设定
// ============================================

/**
 * 城市手写设定
 * 允许只填写少量关键字段，其余部分由服务层结合静态城市数据推导补全。
 */
export interface CityProfile {
  cityId: number;
  description?: string;
  styleTags?: string[];
  specialNotes?: string[];
}

/**
 * 解析后的完整城市设定
 * 供 UI、世界书与叙事系统直接消费。
 */
export interface ResolvedCityProfile {
  cityId: number;
  description: string;
  styleTags: string[];
  specialNotes: string[];
  source: 'authored' | 'inferred';
}

// ============================================
// 旧档兼容：城市场景人物池
// ============================================

/**
 * 旧档兼容字段。
 * 运行时已不再依赖城市侧缓存人物池，保留该结构仅用于读取历史存档。
 */
export const CitySceneCharacterPoolSchema = z.object({
  candidateCharacterIds: z.array(z.string()).default([]),
  generatedAt: z.number().optional(),
  // 兼容旧档中的 preset / template_population / mixed 命名
  source: z
    .enum([
      'runtime',
      'template_fallback',
      'runtime_with_template_fallback',
      'generated',
      'preset',
      'template_population',
      'mixed',
    ])
    .optional(),
});

export type CitySceneCharacterPool = z.infer<typeof CitySceneCharacterPoolSchema>;

// ============================================
// 城市人物引导初始化状态
// ============================================

export const CityCharacterBootstrapStateSchema = z.object({
  sceneCharactersInitialized: z.boolean().default(false),
  initializedAt: z.number().optional(),
  generatedCharacterCount: z.number().int().nonnegative().optional(),
});

export type CityCharacterBootstrapState = z.infer<typeof CityCharacterBootstrapStateSchema>;

// ============================================
// 隐藏住宅锚点
// ============================================

export const CityResidenceAnchorSchema = z.object({
  id: z.string(),
  mapBuildingId: z.string(),
  center: z.tuple([z.number(), z.number()]),
  assignedCharacterId: z.string().optional(),
});

export type CityResidenceAnchor = z.infer<typeof CityResidenceAnchorSchema>;

export const CityStreetAnchorKindSchema = z.enum(['road', 'plaza', 'gate']);

export type CityStreetAnchorKind = z.infer<typeof CityStreetAnchorKindSchema>;

export const CityStreetAnchorSchema = z.object({
  id: z.string(),
  center: z.tuple([z.number(), z.number()]),
  kind: CityStreetAnchorKindSchema.default('road'),
});

export type CityStreetAnchor = z.infer<typeof CityStreetAnchorSchema>;

// ============================================
// 城市场景分配缓存
// ============================================

export const CityBuildingSceneAssignmentCacheEntrySchema = z.object({
  ownerCharacterId: z.string().optional(),
  sceneCharacterIds: z.array(z.string()).default([]),
  source: z.string().default('missing_scene_characters'),
});

export type CityBuildingSceneAssignmentCacheEntry = z.infer<typeof CityBuildingSceneAssignmentCacheEntrySchema>;

export const CitySceneAssignmentStateSchema = z.object({
  dirty: z.boolean().default(true),
  resolvedAt: z.number().optional(),
  lastReason: z.string().optional(),
  buildingResults: z.record(z.string(), CityBuildingSceneAssignmentCacheEntrySchema).optional(),
});

export type CitySceneAssignmentState = z.infer<typeof CitySceneAssignmentStateSchema>;

// ============================================
// 城市状态定义
// ============================================

/**
 * 城市状态 Schema
 * 记录单个城市微观地图的初始化状态和建筑数据
 */
export const CityStateSchema = z.object({
  cityId: z.string(), // 关联的城市 ID (Burg ID)
  isInitialized: z.boolean().default(false), // 是否已完成初始化(核心建筑分配)

  /**
   * 建筑映射表
   * Key: 地图上的多边形唯一 ID (Polygon ID)
   * Value: 建筑详情
   */
  buildings: z.record(z.string(), KnownBuildingSchema).default({}),

  /**
   * 旧档兼容字段
   * 运行时不再从城市侧缓存人物池读取数据，仅在加载历史存档时保留解析能力
   */
  candidatePool: CitySceneCharacterPoolSchema.optional(),

  /**
   * 城市人物引导初始化状态
   * 标记该城市是否已经完成过一次模板角色落地
   */
  characterBootstrap: CityCharacterBootstrapStateSchema.optional(),

  /**
   * 隐藏住宅锚点
   * 基于城市真实建筑生成，但默认不直接作为可交互建筑暴露给玩家
   */
  residenceAnchors: z.record(z.string(), CityResidenceAnchorSchema).default({}),

  /**
   * 街道停留点
   * 供人物户外落位与后续城市内移动逻辑复用
   */
  streetAnchors: z.record(z.string(), CityStreetAnchorSchema).default({}),

  /**
   * 建筑场景人物分配缓存状态
   * 仅记录当前城市建筑场景是否需要重算，以及最近一次成功解析时间
   */
  sceneAssignment: CitySceneAssignmentStateSchema.default({
    dirty: true,
  }),
});

export type CityState = z.infer<typeof CityStateSchema>;
